import { useToast } from 'primevue/usetoast';
import { useSessionStatus } from './useSessionStatus';
import type { ToastMessageOptions } from 'primevue/toast';
import { showEarlyToast } from '@/utils/earlyToast';

// Global flag to track if we've already shown a 401 error toast
// This prevents duplicate toasts when multiple API calls fail simultaneously
let error401ToastShown = false;
let error401ToastTimeout: number | null = null;

/**
 * Reset the 401 toast flag (for testing)
 * @internal
 */
export function reset401ToastFlag(): void {
    error401ToastShown = false;
    if (error401ToastTimeout) {
        clearTimeout(error401ToastTimeout);
        error401ToastTimeout = null;
    }
}

/**
 * Centralized 401 error handler
 * Provides consistent handling of authentication failures across the app
 */
export function use401Handler() {
    // Try to get toast service, but handle gracefully if not available yet (during app initialization)
    let toast: ReturnType<typeof useToast> | null = null;
    let toastAvailable = false;
    
    try {
        toast = useToast();
        toastAvailable = true;
    } catch (error) {
        console.warn('Toast service not available yet (app still initializing) - will use early toast');
    }
    
    const { reportSessionExpired, startHandling401, finishHandling401, hasSessionExpired } = useSessionStatus();

    /**
     * Handle a 401 error with user notification
     * Returns true if handled, false if already handling or session already expired
     */
    const handle401Error = async (context?: string): Promise<boolean> => {
        // Check if user is intentionally logging out or logging in - don't show error toast
        try {
            const { useAuthStore } = await import('@/stores/auth');
            const authStore = useAuthStore();
            
            // Skip 401 handling during logout / login / cross-tab CSRF sync windows
            if (authStore.shouldSuppress401Logout?.() || authStore.isLoggingOut || authStore.isLoggingIn) {
                return false;
            }
            
            // Skip 401 handling if user is not yet authenticated
            if (!authStore.isAuthenticated) {
                return false;
            }
        } catch (error) {
            // If we can't check auth state, continue with normal handling
            console.error('Could not check auth state in 401 handler:', error);
        }
        
        // Prevent multiple concurrent 401 handlers
        if (!startHandling401()) {
            return false;
        }

        try {
            // Mark session as expired
            await reportSessionExpired();

            // Prevent duplicate toast notifications when multiple API calls fail simultaneously
            if (error401ToastShown) {
                return true;
            }

            error401ToastShown = true;

            // Clear the flag after 5 seconds to allow showing again if needed
            if (error401ToastTimeout) {
                clearTimeout(error401ToastTimeout);
            }
            error401ToastTimeout = window.setTimeout(() => {
                error401ToastShown = false;
                error401ToastTimeout = null;
            }, 5000);

            // Show user-friendly notification
            const message: ToastMessageOptions = {
                severity: 'warn',
                summary: 'Session Expired',
                detail: 'Your session has expired. Please log in again.',
                life: 4000 // Auto-hide after 4 seconds
            };

            if (context) {
                message.detail = `Your session has expired. Please log in again.`;
            }

            // Show toast notification (use early toast if PrimeVue not available yet)
            if (toastAvailable && toast) {
                toast.add(message);
            } else {
                // Use framework-independent early toast during initialization
                showEarlyToast({
                    message: message.detail || 'Your session has expired. Please log in again.',
                    type: 'warning',
                    duration: 4000 // Auto-hide after 4 seconds
                });
            }
            
            return true;
        } finally {
            finishHandling401();
        }
    };

    /**
     * Handle 401 with automatic logout
     * Returns true if the session was torn down (caller may redirect to login).
     */
    const handle401WithLogout = async (context?: string): Promise<boolean> => {
        try {
            const { useAuthStore } = await import('@/stores/auth');
            const authStore = useAuthStore();

            if (authStore.shouldSuppress401Logout?.()) {
                return false;
            }

            // CSRF races across tabs often look like session expiry. Try one silent
            // userStatus refresh before tearing down the shared server session.
            if (authStore.isAuthenticated && typeof authStore.tryRecoverSession === 'function') {
                const recovered = await authStore.tryRecoverSession();
                if (recovered) {
                    console.warn('Recovered from 401 via CSRF/session refresh — skipping logout');
                    return false;
                }
            }
        } catch (error) {
            console.error('Could not attempt session recovery after 401:', error);
        }

        const handled = await handle401Error(context);
        
        if (handled) {
            // Import auth store dynamically to avoid circular dependencies
            try {
                const { useAuthStore } = await import('@/stores/auth');
                const authStore = useAuthStore();
                
                // Only logout if authenticated (prevent cascading logouts)
                if (authStore.isAuthenticated) {
                    // logout() already broadcasts 'logout' to other tabs — do not also
                    // broadcast session-invalidated (that caused double remote navigations).
                    await authStore.logout();
                    return true;
                }
            } catch (error) {
                console.error('Failed to trigger logout after 401:', error);
            }
        }
        return false;
    };

    /**
     * Show a notification that we're attempting to refresh the session
     */
    const notifyRefreshAttempt = (): void => {
        if (hasSessionExpired.value) {
            // Don't show refresh messages if session already expired
            return;
        }

        if (toastAvailable && toast) {
            toast.add({
                severity: 'info',
                summary: 'Refreshing Session',
                detail: 'Attempting to refresh your authentication...',
                life: 3000
            });
        } else {
            showEarlyToast({
                message: 'Attempting to refresh your authentication...',
                type: 'info',
                duration: 3000
            });
        }
    };

    /**
     * Show a notification that session refresh was successful
     */
    const notifyRefreshSuccess = (): void => {
        if (toastAvailable && toast) {
            toast.add({
                severity: 'success',
                summary: 'Session Refreshed',
                detail: 'Your session has been refreshed successfully.',
                life: 3000
            });
        } else {
            showEarlyToast({
                message: 'Your session has been refreshed successfully.',
                type: 'success',
                duration: 3000
            });
        }
    };

    return {
        handle401Error,
        handle401WithLogout,
        notifyRefreshAttempt,
        notifyRefreshSuccess
    };
}

